import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileUp, CheckCircle, AlertCircle, Info, Shield } from 'lucide-react';
import Page from '../../../components/Page';
import FileUploadCard from './FileUploadCard';
import { useUploadDataForAnalysisMutation } from '../../../services/api';
import { useOwnerHeaders } from '../../../contexts/OwnerContext';

const SendDataForAnalysisPage: React.FC = () => {
  const ownerHeaders = useOwnerHeaders();
  const [uploadDataForAnalysis, { isLoading: isUploading }] = useUploadDataForAnalysisMutation();
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Scroll para o topo quando a página carrega
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleFileSelected = (file: File) => {
    setUploadedFile(file);
    setUploadStatus('idle');
  };

  const handleFileRemoved = () => {
    setUploadedFile(null);
    setUploadStatus('idle');
  };

  const handleSubmit = async () => {
    if (!uploadedFile || !ownerHeaders?.company) {
      setErrorMessage('Erro: Empresa não identificada. Por favor, faça login novamente.');
      setUploadStatus('error');
      return;
    }

    try {
      setErrorMessage('');
      const formData = new FormData();
      formData.append('file', uploadedFile);

      await uploadDataForAnalysis(formData).unwrap();
      setUploadStatus('success');

      // Reset after success
      setTimeout(() => {
        setUploadedFile(null);
        setUploadStatus('idle');
        setErrorMessage('');
      }, 3000);
    } catch (error: any) {
      setUploadStatus('error');
      setErrorMessage(
        error?.data?.message ||
        'Erro ao enviar arquivo. Verifique o formato e tente novamente.'
      );
    }
  };

  return (
    <Page title="📤 Enviar Dados para Análise">
      <div className="space-y-8">
        {/* Info Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Purpose Card */}
          <motion.div
            className="rounded-xl overflow-hidden shadow-lg border border-blue-100 bg-gradient-to-br from-blue-50 to-cyan-50"
            whileHover={{ shadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}
          >
            <div className="bg-gradient-to-r from-blue-500 to-cyan-600 px-8 py-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                  <Info className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Propósito dessa página</h2>
                  <p className="text-blue-100 text-sm mt-1">Envie seus dados para processamento e análise inteligente</p>
                </div>
              </div>
            </div>
            <div className="p-8">
              <div className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  Esta página permite que você envie arquivos de dados (como CSV, Excel, JSON) para que nosso sistema
                  de análise com inteligência artificial processe e gere insights estratégicos sobre sua malha logística.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                  <div className="flex gap-3 p-4 bg-white rounded-lg border border-blue-200">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Processamento Automático</p>
                      <p className="text-xs text-gray-600 mt-1">Seus dados são automaticamente analisados</p>
                    </div>
                  </div>
                  <div className="flex gap-3 p-4 bg-white rounded-lg border border-blue-200">
                    <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Segurança Garantida</p>
                      <p className="text-xs text-gray-600 mt-1">Todos os dados são criptografados</p>
                    </div>
                  </div>
                  <div className="flex gap-3 p-4 bg-white rounded-lg border border-blue-200">
                    <FileUp className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Múltiplos Formatos</p>
                      <p className="text-xs text-gray-600 mt-1">CSV, Excel, JSON e outros</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Error Message */}
          {uploadStatus === 'error' && errorMessage && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="rounded-xl overflow-hidden shadow-lg border border-red-200 bg-gradient-to-br from-red-50 to-orange-50"
            >
              <div className="bg-gradient-to-r from-red-500 to-orange-600 px-8 py-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                    <AlertCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Erro ao enviar arquivo</h3>
                    <p className="text-red-100 text-sm mt-1">{errorMessage}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Upload Card */}
          <FileUploadCard
            onFileSelected={handleFileSelected}
            onFileRemoved={handleFileRemoved}
            uploadedFile={uploadedFile}
            isUploading={isUploading}
            onSubmit={handleSubmit}
            uploadStatus={uploadStatus}
          />

          {/* Success Message */}
          {uploadStatus === 'success' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="rounded-xl overflow-hidden shadow-lg border border-green-200 bg-gradient-to-br from-green-50 to-emerald-50"
            >
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-8 py-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Arquivo enviado com sucesso!</h3>
                    <p className="text-green-100 text-sm mt-1">Seu arquivo está sendo processado pela IA</p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="space-y-2">
                  <p className="text-sm text-gray-700">
                    ✓ Arquivo recebido e validado
                  </p>
                  <p className="text-sm text-gray-700">
                    ✓ Processamento iniciado
                  </p>
                  <p className="text-sm text-gray-700">
                    ✓ Você receberá uma notificação quando a análise estiver pronta
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Requirements Card */}
          <motion.div
            className="rounded-xl overflow-hidden shadow-lg border border-amber-100 bg-gradient-to-br from-amber-50 to-orange-50"
            whileHover={{ shadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}
          >
            <div className="bg-gradient-to-r from-amber-500 to-orange-600 px-8 py-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Requisitos dos Arquivos</h2>
                  <p className="text-amber-100 text-sm mt-1">Informações importantes para o seu upload</p>
                </div>
              </div>
            </div>
            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-bold text-gray-900 mb-4">Formatos Aceitos</h3>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-sm text-gray-700">
                      <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                      CSV (Comma Separated Values)
                    </li>
                    <li className="flex items-center gap-2 text-sm text-gray-700">
                      <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                      XLSX (Microsoft Excel)
                    </li>
                    <li className="flex items-center gap-2 text-sm text-gray-700">
                      <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                      JSON (JavaScript Object Notation)
                    </li>
                    <li className="flex items-center gap-2 text-sm text-gray-700">
                      <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                      XLS (Excel 97-2003)
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-4">Especificações</h3>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-sm text-gray-700">
                      <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                      Tamanho máximo: 50 MB
                    </li>
                    <li className="flex items-center gap-2 text-sm text-gray-700">
                      <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                      Mínimo de linhas: 10
                    </li>
                    <li className="flex items-center gap-2 text-sm text-gray-700">
                      <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                      Codificação: UTF-8 recomendado
                    </li>
                    <li className="flex items-center gap-2 text-sm text-gray-700">
                      <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                      Cabeçalho: Primeira linha com nomes de colunas
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Tips Card */}
          <motion.div
            className="rounded-xl overflow-hidden shadow-lg border border-purple-100 bg-gradient-to-br from-purple-50 to-pink-50"
            whileHover={{ shadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}
          >
            <div className="bg-gradient-to-r from-purple-500 to-pink-600 px-8 py-6">
              <h2 className="text-2xl font-bold text-white">💡 Dicas para Melhores Resultados</h2>
            </div>
            <div className="p-8">
              <ul className="space-y-3">
                <li className="flex gap-3">
                  <span className="text-lg">📊</span>
                  <p className="text-sm text-gray-700">
                    <strong>Qualidade dos dados:</strong> Certifique-se de que seus dados estão limpos e sem valores faltantes
                  </p>
                </li>
                <li className="flex gap-3">
                  <span className="text-lg">🔍</span>
                  <p className="text-sm text-gray-700">
                    <strong>Dados representativos:</strong> Quanto mais dados você enviar, mais precisa será a análise
                  </p>
                </li>
                <li className="flex gap-3">
                  <span className="text-lg">🏷️</span>
                  <p className="text-sm text-gray-700">
                    <strong>Nomes de colunas claros:</strong> Use nomes descritivos em inglês ou português
                  </p>
                </li>
                <li className="flex gap-3">
                  <span className="text-lg">📅</span>
                  <p className="text-sm text-gray-700">
                    <strong>Período consistente:</strong> Inclua dados de um período de tempo significativo (mínimo 30 dias)
                  </p>
                </li>
              </ul>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </Page>
  );
};

export default SendDataForAnalysisPage;
